import { useState } from 'react';
import { updateProject } from '../api/projectsApi';
import type { Project, UpdateProjectInput } from '../types';

interface UseUpdateProjectReturn {
  isUpdating: boolean;
  error: string | null;
  updateProjectData: (
    projectId: string,
    input: UpdateProjectInput
  ) => Promise<Project | null>;
  clearError: () => void;
}

/**
 * Hook to handle project updates
 */
export function useUpdateProject(): UseUpdateProjectReturn {
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = () => setError(null);

  const updateProjectData = async (
    projectId: string,
    input: UpdateProjectInput
  ): Promise<Project | null> => {
    setIsUpdating(true);
    setError(null);

    try {
      const updatedProject = await updateProject(projectId, input);
      return updatedProject;
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to update project';
      setError(message);
      console.error('[useUpdateProject] Error:', err);
      return null;
    } finally {
      setIsUpdating(false);
    }
  };

  return {
    isUpdating,
    error,
    updateProjectData,
    clearError,
  };
}